import { createAnimationLegacy, createShadowSettingsLegacy } from '../widget/common/schemas/legacy';
import {
    animationFeature,
    borderFeature,
    borderRadiusFeature,
    createBackground,
    scrollEffectFeature,
    shadowFeature,
} from '../features';

import {
    DEFAULT_BORDER_COLOR,
    DEFAULT_FILTER_OVERLAY,
    DEFAULT_OVERLAY_COLOR,
    DEFAULT_OVERLAY_OPACITY,
    DEFAULT_SCROLL_EFFECT,
    DEFAULT_TRANSPARENT_COLOR,
} from './constants';

import type {
    ColumnDeviceDataLegacy,
    ColumnDeviceStylesLegacy,
    DefaultColumn,
    LegacyColumn,
    SpecificColumn,
    ZeroColumn,
} from './schemas';
import type { ColumnEntity } from './schemas';
import { createEntityMeta } from '../utils';
import { compactColumnProps } from './compact';

export type LegacyColumnVariant = 'default' | 'specific';

/**
 * Host-only value the package deliberately doesn't own: the transparent colour
 * literal lives in the colour-picker presets (`TRANSPARENT_VALUE`), so the host
 * may pass its own rather than have the package hard-code a duplicate.
 */
export type CreateLegacyColumnOptions = {
    /** `TRANSPARENT_VALUE` — seed background of a column. */
    transparentColor?: string;
};

const createBorderSides = () => ({
    borderTopWidth: 1,
    borderTopStyle: 'solid',
    borderTopColor: DEFAULT_BORDER_COLOR,
    borderRightWidth: 1,
    borderRightStyle: 'solid',
    borderRightColor: DEFAULT_BORDER_COLOR,
    borderBottomWidth: 1,
    borderBottomStyle: 'solid',
    borderBottomColor: DEFAULT_BORDER_COLOR,
    borderLeftWidth: 1,
    borderLeftStyle: 'solid',
    borderLeftColor: DEFAULT_BORDER_COLOR,
});

const createCorners = () => ({
    borderRadius: 0,
    borderTopLeftRadius: 0,
    borderTopRightRadius: 0,
    borderBottomRightRadius: 0,
    borderBottomLeftRadius: 0,
});

const createDeviceStyles = (transparentColor: string): ColumnDeviceStylesLegacy => ({
    paddingTop: 15,
    paddingBottom: 15,
    paddingLeft: 15,
    paddingRight: 15,
    backgroundColor: transparentColor,
    ...createCorners(),
    ...createBorderSides(),
});

const createDeviceData = (): ColumnDeviceDataLegacy => ({
    backgroundImage: null,
    isBorder: false,
    isIndependentBorders: false,
    isIndependentCorners: false,
    isColorOverflow: false,
    colorOverflow: DEFAULT_OVERLAY_COLOR,
    colorOverflowOpacity: DEFAULT_OVERLAY_OPACITY,
    isFilterOverlay: false,
    filterOverlay: DEFAULT_FILTER_OVERLAY,
});

/**
 * Factory for the current legacy column entity — matches the persisted JSON
 * shape and the host's `columnInstance()` / `specificColumnInstance()` seeds.
 * `id` and `sectionId` are left blank: the host fills them in when it places the
 * column.
 *
 * The `specific` variant is the header/footer column: it spans four of the
 * twelve grid units and leaves alignment and animation to the row, so those
 * fields are seeded `null` and its style bags stay empty (the row paints the
 * background).
 */
export function createLegacyColumn(
    variant: LegacyColumnVariant = 'default',
    options?: CreateLegacyColumnOptions
): LegacyColumn {
    const transparentColor = options?.transparentColor ?? DEFAULT_TRANSPARENT_COLOR;

    if (variant === 'specific') {
        return {
            id: '',
            sectionId: '',
            widgets: [],
            styles: {},
            mobileStyles: {},
            tabletStyles: {},
            data: {
                columnWidth: 4,
                verticalAlign: null,
                horizontalAlign: null,
                isShadow: false,
                isBorder: false,
                isSynchronizeStyle: true,
                isIndependentCorners: false,
                animation: null,
                backgroundImage: null,
                isColorOverflow: false,
                isScrollEffect: false,
                isFilterOverlay: false,
                filterOverlay: DEFAULT_FILTER_OVERLAY,
                scrollEffect: DEFAULT_SCROLL_EFFECT,
                colorOverflow: DEFAULT_OVERLAY_COLOR,
                colorOverflowOpacity: DEFAULT_OVERLAY_OPACITY,
            },
            mobileData: {
                backgroundImage: null,
                isIndependentCorners: false,
                isFilterOverlay: false,
                filterOverlay: DEFAULT_FILTER_OVERLAY,
                colorOverflow: DEFAULT_OVERLAY_COLOR,
                colorOverflowOpacity: DEFAULT_OVERLAY_OPACITY,
            },
            tabletData: {
                backgroundImage: null,
                isIndependentCorners: false,
                isFilterOverlay: false,
                filterOverlay: DEFAULT_FILTER_OVERLAY,
                colorOverflow: DEFAULT_OVERLAY_COLOR,
                colorOverflowOpacity: DEFAULT_OVERLAY_OPACITY,
            },
        };
    }

    return {
        id: '',
        sectionId: '',
        widgets: [],
        styles: {
            ...createDeviceStyles(transparentColor),
            borderWidth: 1,
            borderStyle: 'solid',
            borderColor: DEFAULT_BORDER_COLOR,
        },
        data: {
            columnWidth: 12,
            columnWidthPer: 100,
            verticalAlign: 'M',
            horizontalAlign: 'L',
            isShadow: false,
            shadowSettings: createShadowSettingsLegacy(),
            isBorder: false,
            isSynchronizeStyle: true,
            isIndependentCorners: false,
            isIndependentBorders: false,
            animation: createAnimationLegacy(),
            backgroundImage: null,
            isColorOverflow: false,
            isScrollEffect: false,
            isFilterOverlay: false,
            filterOverlay: DEFAULT_FILTER_OVERLAY,
            scrollEffect: DEFAULT_SCROLL_EFFECT,
            colorOverflow: DEFAULT_OVERLAY_COLOR,
            colorOverflowOpacity: DEFAULT_OVERLAY_OPACITY,
        },
        mobileStyles: createDeviceStyles(transparentColor),
        mobileData: createDeviceData(),
        tabletStyles: createDeviceStyles(transparentColor),
        tabletData: createDeviceData(),
    };
}

/**
 * v0 factories. `id` and `parentId` are left blank: the host fills them in when
 * it places the column.
 */

/**
 * Every responsive leaf is seeded on all three breakpoints — see the migration.
 * Takes a factory rather than a value so the three device keys never end up
 * aliasing one object.
 */
const everywhere = <T>(make: () => T) => ({ desktop: make(), tablet: make(), mobile: make() });

const baseDefaults = {
    id: '',
    parentId: '',
    entityType: 'column' as const,
    children: [] as unknown[],
    version: 0 as const,
};

export const createDefaultColumn = (options?: CreateColumnOptions): DefaultColumn => ({
    ...baseDefaults,
    meta: createEntityMeta(),
    entitySubType: 'default',
    props: compactColumnProps({
        width: { value: 12, unit: 'col' },
        syncWithDesktop: true,

        verticalAlign: 'middle',
        horizontalAlign: 'left',

        padding: everywhere(() => ({ value: { top: 15, right: 15, bottom: 15, left: 15 }, unit: 'px' as const })),
        // Both are features, so the version travels with the value. The contour
        // is off, so it stores its switch alone; switching it on seeds a visible
        // 1px side (`createBorderSide`).
        borderRadius: borderRadiusFeature.createDefault(),
        border: borderFeature.createDefault(),
        background: createBackground({ transparentColor: options?.transparentColor }),

        shadow: { ...shadowFeature.createDefault(), settings: createShadowSettingsLegacy() },
        scrollEffect: { ...scrollEffectFeature.createDefault(), value: DEFAULT_SCROLL_EFFECT },
        animation: animationFeature.createDefault(),
    }),
});

/** The header/footer slot: a width and the alignment its position in the row implies. */
export const createSpecificColumn = (): SpecificColumn => ({
    ...baseDefaults,
    meta: createEntityMeta(),
    entitySubType: 'specific',
    props: {
        width: { value: 4, unit: 'col' },
        horizontalAlign: null,
    },
});

export const createZeroColumn = (): ZeroColumn => ({
    ...baseDefaults,
    meta: createEntityMeta(),
    entitySubType: 'zero',
    props: {},
});

/**
 * Host-only value the package deliberately doesn't own: the transparent colour
 * literal lives in the colour-picker presets (`TRANSPARENT_VALUE`).
 */
export type CreateColumnOptions = CreateLegacyColumnOptions;
export type ColumnVariant = 'default' | 'specific' | 'zero';

export function createColumn(variant: ColumnVariant = 'default', options?: CreateColumnOptions): ColumnEntity {
    if (variant === 'specific') return createSpecificColumn();
    if (variant === 'zero') return createZeroColumn();

    return createDefaultColumn(options);
}
